import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bell, BellOff, CloudDrizzle, CloudRain, Sun, Volume2, VolumeX } from "lucide-react";
import { beep, intensity, rainOutlook } from "@/lib/weather";
import { useT } from "@/lib/i18n";

const NOTIFIED = "kart_wx_notified";

const useStored = (key, init) => {
  const [v, setV] = useState(() => localStorage.getItem(key) ?? init);
  return [v, (x) => { localStorage.setItem(key, x); setV(x); }];
};

const AlertControls = ({ sound, setSound }) => {
  const { t } = useT();
  const [perm, setPerm] = useState(typeof Notification === "undefined" ? "unsupported" : Notification.permission);
  const ask = async () => setPerm(await Notification.requestPermission());
  return (
    <div className="flex items-center gap-2 shrink-0">
      {perm !== "unsupported" && perm !== "granted" && (
        <button type="button" data-testid="weather-notify-btn" onClick={ask} title={t("wx_enable_notify")} className="flex items-center gap-1.5 h-8 px-2.5 rounded-sm border wx-bd font-mono text-[11px] uppercase hover:bg-white/10 transition-colors duration-200">
          <BellOff className="w-4 h-4" /> <span className="hidden sm:inline">{t("wx_enable_notify")}</span>
        </button>
      )}
      {perm === "granted" && <span data-testid="weather-notify-on" className="flex items-center gap-1 font-mono text-[10px] uppercase opacity-80"><Bell className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t("wx_notify_on")}</span></span>}
      <button type="button" data-testid="weather-sound-btn" onClick={() => { const on = sound !== "1"; setSound(on ? "1" : "0"); if (on) beep(); }} title={t("wx_sound")}
        className="h-8 w-8 flex items-center justify-center rounded-sm border wx-bd hover:bg-white/10 transition-colors duration-200">
        {sound === "1" ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 opacity-60" />}
      </button>
    </div>
  );
};

function useRainNotify(o, place, title, body, sound) {
  useEffect(() => {
    if (o?.state !== "incoming" && o?.state !== "possible") return;
    const key = `${place}-${o.state}-${Math.round(o.start / 900)}`;
    if (localStorage.getItem(NOTIFIED) === key) return;
    localStorage.setItem(NOTIFIED, key);
    if (typeof Notification !== "undefined" && Notification.permission === "granted") new Notification(`${title} · ${place}`, { body, tag: "kart-rain" });
    if (sound === "1") beep();
  }, [o?.state, o?.start, place]); // eslint-disable-line react-hooks/exhaustive-deps
}

function useTexts(o) {
  const { t } = useT();
  const level = o?.peak ? t(`wx_int_${intensity(o.peak)}`) : "";
  const peak = o?.peak ? `${level} · max ${o.peak.toFixed(1)} mm/h` : "";
  if (!o) return { title: t("wx_no_minutely"), sub: "" };
  if (o.state === "incoming") return { title: t("wx_rain_in").replace("{m}", o.mins), sub: peak };
  if (o.state === "possible") return { title: t("wx_rain_possible").replace("{m}", o.mins), sub: [t("wx_rain_prob").replace("{p}", o.pop), peak].filter(Boolean).join(" · ") };
  if (o.state === "raining") return { title: t("wx_raining"), sub: [o.mins ? t("wx_stop_in").replace("{m}", o.mins) : t("wx_rain_continues"), peak].filter(Boolean).join(" · ") };
  return { title: t("wx_no_rain"), sub: t("wx_no_rain_sub") };
}

const STYLES = {
  red: "bg-[#FF3B30] border-[#FF3B30] text-white wx-pulse px-3 py-2 sm:px-4 gap-x-3 gap-y-2",
  amber: "bg-[#FF9500] border-[#FF9500] text-[#0A0B10] px-3 py-2 sm:px-4 gap-x-3 gap-y-2",
  green: "bg-[#10B981]/10 border-[#10B981]/40 text-[#10B981] px-3 py-1.5 gap-x-2.5 gap-y-1.5",
};

export const RainAlert = ({ data, place, link = false }) => {
  const { t } = useT();
  const [sound, setSound] = useStored("kart_wx_sound", "1");
  const [, tick] = useState(0);
  useEffect(() => { const id = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(id); }, []);
  const o = rainOutlook(data?.minutely, data?.hourly);
  const { title, sub } = useTexts(o);
  useRainNotify(o, place, title, sub, sound);
  const tone = o?.state === "possible" ? "amber" : o?.state === "incoming" || o?.state === "raining" ? "red" : "green";
  const Icon = tone === "amber" ? CloudDrizzle : tone === "red" ? CloudRain : Sun;
  return (
    <div data-testid={link ? "global-rain-alert" : "weather-rain-alert"} data-state={o?.state || "none"} className={`rounded-sm border flex flex-wrap items-center ${STYLES[tone]}`}>
      <Icon className={`shrink-0 ${tone === "green" ? "w-5 h-5" : "w-7 h-7"}`} />
      <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <div data-testid="weather-rain-title" className={`font-heading font-extrabold uppercase leading-none ${tone === "green" ? "text-base sm:text-lg" : "text-xl sm:text-2xl"}`}>{title}</div>
        <div data-testid="weather-rain-sub" className="font-mono text-[10px] sm:text-[11px] uppercase opacity-90">{[link ? place : "", sub].filter(Boolean).join(" · ")}</div>
      </div>
      <AlertControls sound={sound} setSound={setSound} />
      {link && (
        <Link to="/weather" data-testid="global-rain-alert-open" title={t("wx_open")} className="h-8 px-2.5 flex items-center gap-1 rounded-sm border wx-bd font-mono text-[11px] uppercase hover:bg-white/10 transition-colors duration-200">
          <span className="hidden sm:inline">{t("wx_open")}</span> <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
};
